import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApi } from '../services/apiServices';
import { useLanguage } from '../contexts/LanguageContext';
// Remove the helper import since we're using direct API call
// import { fetchCountriesEsim } from '../Utils/helper';
 
const DestinationDropdown = ({ isActive = false, onNavItemClick }) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [countriesEsim, setCountriesEsim] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSearch, setSelectedSearch] = useState('popular');
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [userChangedCurrency, setUserChangedCurrency] = useState(false);
  const dropdownRef = useRef(null);

  // Language mapping function to convert app language codes to API language codes
  const getApiLanguageCode = (appLanguage) => {
    const languageMap = {
      'ar': 'ar',     // Arabic
      'da': 'da',     // Danish
      'de': 'de',     // German
      'en': 'en',     // English
      'es': 'es',     // Spanish
      'fr': 'fr',     // French
      'he': 'he',     // Hebrew
      'it': 'it',     // Italian
      'ja': 'ja',     // Japanese
      'ko': 'ko',     // Korean
      'nl': 'nl',     // Dutch
      'no': 'no',     // Norwegian
      'pl': 'pl',     // Polish
      'pt': 'pt',     // Portuguese
      'ru': 'ru',     // Russian
      'sv': 'sv',     // Swedish
      'tr': 'tr',     // Turkish
      'zh': 'zh_CN'   // Chinese (Simplified)
    };
    return languageMap[appLanguage] || 'en'; // Default to English if not found
  };

  useEffect(() => {
    const storedCurrency = localStorage.getItem('selectedCurrency');
    if (storedCurrency) {
      setSelectedCurrency(storedCurrency);
    }
  }, []);

  useEffect(() => {
    const handleCurrencyChange = () => {
      const currency = localStorage.getItem('selectedCurrency') || 'USD';
      setSelectedCurrency(currency);
      setUserChangedCurrency(true); 
    };
    window.addEventListener('cartUpdated', handleCurrencyChange);
    return () => window.removeEventListener('cartUpdated', handleCurrencyChange);
  }, []);
 
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoading(true);
        setError(null);
        let apiFilter = '';
        if (selectedSearch === 'popular') {
          apiFilter = 'popular';
        } else if (selectedSearch === 'regional') {
          apiFilter = 'region';
        }
        
        const searchParam = searchQuery.trim() || '';
        
        const params = new URLSearchParams({
          count: 10,
          page: 1,
          filter: apiFilter,
          search: searchParam,
        });
        
        const currentCurrency = localStorage.getItem('selectedCurrency') || 'USD';
        const apiLanguage = getApiLanguageCode(currentLanguage);
        
        if (currentCurrency !== 'USD') {
          params.append('currency', currentCurrency);
        }
        
        params.append('lang', apiLanguage);
        
        const response = await getApi(`/api/countries?${params.toString()}`);
        
        if (response?.status && response?.data?.countries) {
          setCountriesEsim(response.data.countries);
        } else {
          setCountriesEsim([]);
          setError('Failed to fetch countries');
        }
      } catch (error) {
        console.error('Error fetching countries:', error);
        setError('Failed to fetch countries');
        setCountriesEsim([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
  }, [selectedSearch, searchQuery, selectedCurrency, userChangedCurrency, currentLanguage]); 
 
  
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('destination-open');
    } else {
      document.body.classList.remove('destination-open');
    }
 
    return () => {
      document.body.classList.remove('destination-open');
    };
  }, [isOpen]);
 
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredCountries(countriesEsim);
    } else {
      const filtered = countriesEsim.filter(country =>
        country.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredCountries(filtered);
    }
  }, [searchQuery, countriesEsim]);
 
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
 
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
 
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
 
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchTypeChange = (search) => {
    setSelectedSearch(search);
    setSearchQuery(''); 
  };
 
  const toggleDropdown = () => {
   
    setIsOpen(!isOpen);
  };
  const closeDropdown = (targetPath) => {
    setIsOpen(false);
    
    if (onNavItemClick) {
      onNavItemClick(targetPath);
    }
  };
 
  return (
    <li className={`nav-item dropdown ${isActive ? 'active' : ''}`} ref={dropdownRef}>
      <a
        className="nav-link dropdown-toggle"
        href="#"
        id="navbarDropdown"
        role="button"
        onClick={(e) => {
          e.preventDefault();
          toggleDropdown();
        }}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        {t('header.nav.destinations')}<span className="r4l-caret" aria-hidden="true"></span>
      </a>
      {isOpen && (
        <div className="dropdown-menu show destination-box" aria-labelledby="navbarDropdown">
          <div className="">
            <span className="drop-arrow"><img src={window.getPublicUrl('images/drop-arrow2.svg')} alt="Dropdown indicator" /></span>
            <h3>{t('header.destinations.title')}</h3>
            <div className="banner-search">
              <button className="location">
                <img src={window.getPublicUrl('images/location.svg')} alt="Location" />
              </button>
              <input
                type="text"
                className="search"
                placeholder={t('header.destinations.searchPlaceholder')}
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <button className="search">
                <img src={window.getPublicUrl('images/Search2.svg')} alt="Search" />
              </button>
            </div>
            <ul>
              <li>
                <a 
                  href="" 
                  onClick={(e) => { e.preventDefault(); handleSearchTypeChange('popular'); }}
                  className={selectedSearch === 'popular' ? 'active' : ''}
                >
                  {t('header.destinations.popular')}
                </a>
              </li>
              <li>
                <a 
                  href="" 
                  onClick={(e) => { e.preventDefault(); handleSearchTypeChange('regional'); }}
                  className={selectedSearch === 'regional' ? 'active' : ''}
                >
                  {t('header.destinations.regionalPlans')}
                </a>
              </li>
            </ul>
 
            <div className="row">
              {loading ? (
                <div className="col-sm-12">{t('header.destinations.loading')}</div>
              ) : error ? (
                <div className="col-sm-12">{t('header.destinations.error')}</div>
              ) : filteredCountries.length === 0 ? (
                <div className="col-sm-12">{t('header.destinations.noDestinationsFound')}</div>
              ) : (
                filteredCountries.slice(0, 6).map((country, index) => (
                  <div className="col-sm-4" key={index}>
                    <Link
                      to={`/destination-detail/${country.iso3}`}
                      className="destination-link"
                      state={{ countryData: country }}
                      onClick={() => closeDropdown(`/destination-detail/${country.iso3}`)}
                    >
                      <div className="destination-card">
                        <span>
                          <img src={country.flag} alt={country.name} />
                        </span>
                        <h4>{country.name}</h4>
                      </div>
                    </Link>
                  </div>
                ))
              )}
 
              <div className="col-sm-12 text-center mt-3">
                <Link to="/destination" onClick={() => closeDropdown('/destination')}>
                  <button className="view-all destination">{t('header.destinations.viewAll')}</button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </li>
  );
};
 
export default DestinationDropdown;
 